/**
 * Hook qui garantit que user + profile sont prêts avant d'autoriser les opérations
 * 
 * Ce hook résout la race condition entre le chargement du user (JWT) et du profile (Supabase),
 * en fournissant un état `isReady` qui est true UNIQUEMENT quand les deux sont chargés.
 * 
 * Usage:
 *   const { isReady, user, profile } = useAuthReady();
 *   
 *   if (!isReady) return <LoadingScreen />;
 *   // Ici: GARANTI que user ET profile existent
 * 
 * @returns {Object} État d'authentification complet
 * @returns {boolean} isReady - true si user ET profile sont chargés
 * @returns {User | null} user - Utilisateur Supabase
 * @returns {Profile | null} profile - Profil utilisateur
 * @returns {boolean} loading - État de chargement
 */

import { useAuth } from '@/components/auth/AuthProvider';

export function useAuthReady() {
  const context = useAuth();
  
  // Protection contre l'utilisation en dehors du contexte
  if (!context) {
    throw new Error('useAuthReady must be used within an AuthProvider');
  }
  
  const { user, profile, loading } = context;
  
  // isReady = true UNIQUEMENT si:
  // 1. Loading terminé (pas en cours de chargement)
  // 2. User existe (JWT valide)
  // 3. Profile existe (données Supabase chargées)
  const isReady = !loading && user != null && profile != null;
  
  return {
    isReady,
    user,
    profile,
    loading
  };
}

/**
 * Hook variant strict: Throw error si pas authentifié
 * 
 * Utiliser ce hook dans les composants qui NÉCESSITENT une authentification.
 * Si l'utilisateur n'est pas authentifié, une erreur est levée.
 * 
 * Usage:
 *   const { user, profile } = useAuthRequired();
 *   // Ici: GARANTI que user ET profile existent (sinon erreur)
 */
export function useAuthRequired() {
  const { isReady, user, profile } = useAuthReady();
  
  if (!isReady) {
    throw new Error('useAuthRequired: Authentification requise mais user ou profile manquant');
  }
  
  return {
    user: user!,
    profile: profile!
  };
}

export default useAuthReady;

